import { useEffect, useState } from "react";
import { authClient } from "../utils/amplifyClient";
import * as Sentry from "@sentry/browser";
import { useUserContext } from "../contexts/UserContext";
import {
    listBusinessAdmins as listBusinessAdminsQuery,
    listCompanies as listCompaniesQuery,
} from "../graphql/queries";
import {
    createBusinessAdmin as createBusinessAdminMutation,
    deleteBusinessAdmin as deleteBusinessAdminMutation,
} from "../graphql/mutations";
import { BusinessAdmin, CreateBusinessAdminInput } from "../types/schema";

import "../css/BusinessAdminHomepage.css";



export const BusinessAdminHomepage = () => {
    const { businessAdmin } = useUserContext();

    const [admins, setAdmins] = useState<BusinessAdmin[]>([]);
    const [companyCount, setCompanyCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    // Enroll modal state
    const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
    const [enrollName, setEnrollName] = useState("");
    const [enrollEmail, setEnrollEmail] = useState("");
    const [enrollRole, setEnrollRole] = useState("platform-admin");
    const [isCreating, setIsCreating] = useState(false);

    // Delete confirmation state
    const [deleteTarget, setDeleteTarget] = useState<BusinessAdmin | null>(null);

    // Resend invite tracking
    const [resentAdminIds, setResentAdminIds] = useState<string[]>([]);

    // ─── Data Fetching ───────────────────────────────────
    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [adminsResponse, companiesResponse] = await Promise.all([
                authClient.graphql({ query: listBusinessAdminsQuery }),
                authClient.graphql({ query: listCompaniesQuery }),
            ]);

            const adminItems = adminsResponse.data.listBusinessAdmins?.items ?? [];
            setAdmins(adminItems.filter(Boolean) as BusinessAdmin[]);

            const companyItems = companiesResponse.data.listCompanies?.items ?? [];
            setCompanyCount(companyItems.length);
        } catch (error) {
            console.error("Error fetching business admin data:", error);
            Sentry.captureException(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // ─── Enroll Admin Handler ────────────────────────────
    const handleEnrollAdmin = async () => {
        if (!enrollName.trim() || !enrollEmail.trim() || isCreating) return;
        setIsCreating(true);
        try {
            // NOTE: cognitoId uses a placeholder because it's a GSI key in DynamoDB
            // and cannot be an empty string. The placeholder is replaced with the real
            // cognitoId when the admin signs up through Cognito (handled in Login.tsx).
            const input: CreateBusinessAdminInput = {
                name: enrollName.trim(),
                email: enrollEmail.trim().toLowerCase(),
                cognitoId: `pending-${Date.now()}`,
                role: enrollRole,
                domain: window.location.origin,
            };

            await authClient.graphql({
                query: createBusinessAdminMutation,
                variables: { input },
            });

            // Reset form and close modal
            setEnrollName("");
            setEnrollEmail("");
            setEnrollRole("platform-admin");
            setIsEnrollModalOpen(false);

            // Refresh the list
            await fetchData();
        } catch (error) {
            console.error("Error creating business admin:", error);
            Sentry.captureException(error);
        } finally {
            setIsCreating(false);
        }
    };

    // ─── Delete Admin Handler ────────────────────────────
    const handleDeleteAdmin = async () => {
        if (!deleteTarget?.id) return;
        try {
            await authClient.graphql({
                query: deleteBusinessAdminMutation,
                variables: { input: { id: deleteTarget.id } },
            });
            setDeleteTarget(null);
            await fetchData();
        } catch (error) {
            console.error("Error deleting business admin:", error);
            Sentry.captureException(error);
        }
    };

    // ─── Helpers ─────────────────────────────────────────
    const isConfirmed = (admin: BusinessAdmin) =>
        !!admin.cognitoId && !admin.cognitoId.startsWith("pending-");

    const handleResendInvite = async (admin: BusinessAdmin) => {
        if (!admin.id) return;
        try {
            setResentAdminIds((prev) => [...prev, admin.id]);

            // Delete the old record
            await authClient.graphql({
                query: deleteBusinessAdminMutation,
                variables: { input: { id: admin.id } },
            });

            // Recreate with a fresh pending cognitoId
            await authClient.graphql({
                query: createBusinessAdminMutation,
                variables: {
                    input: {
                        email: admin.email.toLowerCase(),
                        name: admin.name,
                        cognitoId: `pending-${Date.now()}`,
                        role: admin.role || "platform-admin",
                        domain: window.location.origin,
                    },
                },
            });

            await fetchData();
        } catch (error) {
            console.error("Error resending invite:", error);
            Sentry.captureException(error);
        }
    };

    const formatDate = (dateString?: string | null) => {
        if (!dateString) return "—";
        return new Date(dateString).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    };

    const todayFormatted = new Date().toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
    });

    // ─── Render ──────────────────────────────────────────
    return (
        <div className="page-container bah-container">
            {/* Page Header */}
            <div className="bah-page-header">
                <div>
                    <h1 className="bah-page-title">
                        Welcome, {businessAdmin?.name || "Admin"}
                    </h1>
                    <p className="bah-page-subtitle">{todayFormatted}</p>
                </div>
                <button
                    className="bah-btn-add"
                    onClick={() => setIsEnrollModalOpen(true)}
                >
                    <svg viewBox="0 0 16 16" fill="currentColor">
                        <path d="M8 1a1 1 0 011 1v5h5a1 1 0 110 2H9v5a1 1 0 11-2 0V9H2a1 1 0 010-2h5V2a1 1 0 011-1z" />
                    </svg>
                    Enroll Admin
                </button>
            </div>

            {/* Stats Cards */}
            <div className="bah-stats-row">
                <div className="bah-stat-card">
                    <p className="bah-stat-label">Total Business Admins</p>
                    <p className="bah-stat-value">{admins.length}</p>
                    <p className="bah-stat-detail">
                        <span className="bah-dot-green" />
                        Platform administrators
                    </p>
                </div>
                <div className="bah-stat-card">
                    <p className="bah-stat-label">Companies Managed</p>
                    <p className="bah-stat-value">{companyCount}</p>
                    <p className="bah-stat-detail">
                        <span className="bah-dot-green" />
                        Active companies
                    </p>
                </div>
            </div>

            {/* Table Section */}
            <div className="bah-table-section">
                <div className="bah-table-header">
                    <h2 className="bah-section-title">
                        Business Admins{" "}
                        <span className="bah-section-count">{admins.length}</span>
                    </h2>
                </div>

                <div className="bah-table-wrapper">
                    {isLoading ? (
                        <div className="bah-loading">Loading admin data…</div>
                    ) : admins.length === 0 ? (
                        <div className="bah-empty">
                            No business admins found. Click "Enroll Admin" to add one.
                        </div>
                    ) : (
                        <table className="bah-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Status</th>
                                    <th>Role</th>
                                    <th>Created</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {admins.map((admin) => (
                                    <tr key={admin.id}>
                                        <td>
                                            <span className="bah-admin-name">
                                                {admin.name}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="bah-admin-email">
                                                {admin.email}
                                            </span>
                                        </td>
                                        <td>
                                            {isConfirmed(admin) ? (
                                                <span className="bah-badge bah-badge-confirmed">Confirmed</span>
                                            ) : resentAdminIds.includes(admin.id) ? (
                                                <span className="bah-badge bah-badge-confirmed">Invite Sent</span>
                                            ) : (
                                                <span
                                                    className="bah-badge bah-badge-unconfirmed"
                                                    style={{ cursor: "pointer" }}
                                                    title="Click to resend invite"
                                                    onClick={() => handleResendInvite(admin)}
                                                >
                                                    Unconfirmed ✉
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <span className="bah-badge">
                                                {admin.role}
                                            </span>
                                        </td>
                                        <td>{formatDate(admin.createdAt)}</td>
                                        <td>
                                            <div className="bah-actions">
                                                <button
                                                    className="bah-action-btn danger"
                                                    title="Delete admin"
                                                    onClick={() => setDeleteTarget(admin)}
                                                    aria-label={`Delete ${admin.name}`}
                                                >
                                                    🗑
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* ─── Enroll Admin Modal ─── */}
            {isEnrollModalOpen && (
                <div
                    className="bah-overlay"
                    onClick={() => {
                        setIsEnrollModalOpen(false);
                        setEnrollName("");
                        setEnrollEmail("");
                        setEnrollRole("platform-admin");
                    }}
                >
                    <div className="bah-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="bah-modal-header">
                            <h2>Enroll Business Admin</h2>
                            <button
                                className="bah-modal-close"
                                onClick={() => {
                                    setIsEnrollModalOpen(false);
                                    setEnrollName("");
                                    setEnrollEmail("");
                                    setEnrollRole("platform-admin");
                                }}
                                aria-label="Close modal"
                            >
                                ×
                            </button>
                        </div>
                        <div className="bah-modal-body">
                            <div className="bah-field">
                                <label>Name</label>
                                <input
                                    type="text"
                                    value={enrollName}
                                    placeholder="e.g. Jane Smith"
                                    onChange={(e) => setEnrollName(e.target.value)}
                                />
                            </div>
                            <div className="bah-field">
                                <label>Email Address</label>
                                <input
                                    type="email"
                                    value={enrollEmail}
                                    placeholder="e.g. jane@zorva.com"
                                    onChange={(e) => setEnrollEmail(e.target.value)}
                                />
                            </div>
                            <div className="bah-field">
                                <label>Role</label>
                                <select
                                    value={enrollRole}
                                    onChange={(e) => setEnrollRole(e.target.value)}
                                >
                                    <option value="platform-admin">Platform Admin</option>
                                    <option value="business-admin">Business Admin</option>
                                    <option value="support-admin">Support Admin</option>
                                </select>
                            </div>
                        </div>
                        <div className="bah-modal-footer">
                            <button
                                className="bah-btn-cancel"
                                onClick={() => {
                                    setIsEnrollModalOpen(false);
                                    setEnrollName("");
                                    setEnrollEmail("");
                                    setEnrollRole("platform-admin");
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="bah-btn-submit"
                                disabled={!enrollName.trim() || !enrollEmail.trim() || isCreating}
                                onClick={handleEnrollAdmin}
                            >
                                {isCreating ? "Creating…" : "Enroll Admin"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Delete Confirmation Modal ─── */}
            {deleteTarget && (
                <div
                    className="bah-overlay"
                    onClick={() => setDeleteTarget(null)}
                >
                    <div className="bah-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="bah-modal-header">
                            <h2>Confirm Delete</h2>
                            <button
                                className="bah-modal-close"
                                onClick={() => setDeleteTarget(null)}
                                aria-label="Close modal"
                            >
                                ×
                            </button>
                        </div>
                        <div className="bah-confirm-body">
                            <p>
                                Are you sure you want to delete{" "}
                                <span className="bah-confirm-name">
                                    {deleteTarget.name}
                                </span>
                                ?
                            </p>
                            <p className="bah-confirm-warning">
                                This action cannot be undone.
                            </p>
                        </div>
                        <div className="bah-modal-footer">
                            <button
                                className="bah-btn-cancel"
                                onClick={() => setDeleteTarget(null)}
                            >
                                Cancel
                            </button>
                            <button
                                className="bah-btn-delete"
                                onClick={handleDeleteAdmin}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
